import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import DeleteModal from "components/shared/DeleteModal";
import { PortalType } from "contexts/portal.context";
import { getCognitoUserAttribute } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchChannelCompensationGroups, fetchChannels, fetchDeleteChannel } from "store/channelStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";
import type { ChannelType } from "store/types";
import { EPortalType } from "store/types";

import ChannelsHeader from "./ChannelsHeader";
import ChannelsList from "./ChannelsList";
import ChannelsSearch from "./ChannelsSearch";
import CreateChannelModal from "./CreateChannelModal";
import type { IChannel } from "./types";
import { DeleteChannelDescription, useFormatChannels } from "./utils";

{
    /** Utils */
}
export enum ChannelKind {
    PHYSICAL = 1,
    DIGITAL = 2,
    RIGHTS_TRANSFER = 3,
    COLLECTIVE_MANAGEMENT = 4,
    OTHER = 5,
}
export enum CalculationMethod {
    PROPORTIONAL = 1,
    PACKAGE = 2,
    SUBSCRIPTION = 3,
}
export enum CollectiveManagementKind {
    SOFIA = "SOFIA",
    CFC = "CFC",
    OTHER = "OTHER",
}

export const channelKindsI18n = (): Record<ChannelKind, string> => {
    return {
        1: t("NewChannel.Physical"),
        2: t("NewChannel.Numerical"),
        3: t("NewChannel.RightTransfer"),
        4: t("NewChannel.AuthorAssociation"),
        5: t("Settings.Other"),
    };
};
export const calculationMethodsI18n = (): Record<CalculationMethod, string> => {
    return {
        1: t("NewChannel.Proportional"),
        2: t("NewChannel.Package"),
        3: t("NewChannel.Subscription"),
    };
};
export const channelTypesI18n = (): Record<ChannelType, string> => {
    return { 1: t("NewChannel.Quantity"), 2: t("NewChannel.Amount"), 3: t("NewChannel.Folio") };
};

const Channels = () => {
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { getCompanySettings } = useCompanySettings();
    const usesGroups = getCompanySettings("uses_interchannel_compensation");
    const { user } = useAuthenticator((context) => [context.user]);
    const ability = useAbility();
    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const usesAgreementsOnly = getCompanySettings("uses_agreements_only");
    // Can manage channels AND configure company settings (only OWNER has both)
    const canConfigureChannels = ability.can("update", "Channel") && ability.can("update", "CompanySetting");
    const isOwnerLevel = canConfigureChannels || accountType === "admin";

    const hasEditPermission =
        (portalType === EPortalType.BOOK && !usesAgreementsOnly) ||
        (usesAgreementsOnly && portalType === EPortalType.BOOK && isOwnerLevel);

    const { channels, channelCompensationGroups } = useSelector((state: RootState) => state.channels);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentCategory, setCurrentCategory] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [queryIsTyping, setQueryIsTyping] = useState<boolean | null>(null);
    const [timeOut, setTimeOut] = useState<NodeJS.Timeout | undefined>();
    const [onTargetChannelId, setOnTargetChannelId] = useState<number | null>(null);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [currentChannel, setCurrentChannel] = useState<IChannel | undefined>(undefined);
    const [onCreateChannel, setOnCreateChannel] = useState(false);

    const handleSearchQuery = (e: any) => {
        setSearchQuery(e);
        // Debounce call endpoint when search
        if (!queryIsTyping) {
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
            setQueryIsTyping(true);
        } else {
            clearTimeout(timeOut);
            setTimeOut(
                setTimeout(() => {
                    setQueryIsTyping(false);
                }, 700),
            );
        }
    };

    const handleOpenChannel = (currentChannel: IChannel) => {
        if (onTargetChannelId !== currentChannel.id) {
            setOnTargetChannelId(currentChannel.id);
            setCurrentChannel(currentChannel);
        } else {
            setOnTargetChannelId(null);
            setCurrentChannel(undefined);
        }
    };
    const handleChannelDelete = async () => {
        if (currentChannel) {
            const response = await dispatch(fetchDeleteChannel(currentChannel.id));
            if (response.meta.requestStatus === "fulfilled") {
                setOpenDeleteModal(false);
            }
        }
    };

    /**
     * Used mainly for Press companies, where we need to change channel name
     */
    const cleanedChannels = useFormatChannels(channels, portalType);

    /**
     * Filter channels based on search query or category chosen:
     */
    const filteredChannels = useMemo(() => {
        if (searchQuery && !currentCategory) {
            return cleanedChannels.filter((channel) =>
                channel.name.toLocaleLowerCase().includes(searchQuery.toLowerCase()),
            );
        } else if (currentCategory && !searchQuery) {
            return cleanedChannels.filter((channel) => channel.kind == currentCategory.id);
        } else if (currentCategory && searchQuery) {
            return cleanedChannels.filter(
                (channel) =>
                    channel.kind == currentCategory.id &&
                    channel.name.toLocaleLowerCase().includes(searchQuery.toLowerCase()),
            );
        } else return [...cleanedChannels].reverse();
    }, [searchQuery, cleanedChannels, currentCategory]);

    useEffect(() => {
        if (!channelCompensationGroups.length) {
            dispatch(fetchChannelCompensationGroups());
        }
    }, []);

    const channelDiv = useMemo(() => {
        const currentChannelDiv = document.querySelector(`.channel-${onTargetChannelId}`);
        return currentChannelDiv;
    }, [onTargetChannelId]);

    useEffect(() => {
        if (onCreateChannel && usesGroups) {
            channelDiv?.scrollIntoView({
                behavior: `smooth`,
            });
            setOnCreateChannel(false);
        }
    }, [onCreateChannel]);

    const { paginatedData: paginatedChannels, paginatedElRef } = useInfiniteScroll(filteredChannels, true);

    return (
        <>
            <div className="channels-scroller" ref={paginatedElRef}>
                <div className="channels-body">
                    <div className="channels-top">
                        <ChannelsHeader
                            onCreateChannel={() => {
                                setOpenCreateModal(true);
                            }}
                            title={`${t("Channels.Channels")} (${cleanedChannels.length})`}
                            subtitle={t("NewChannel.ChannelsSubtitle")}
                            btnText={t("NewChannel.NewChannel")}
                            hasEditPermission={hasEditPermission}
                        />
                        {portalType === EPortalType.BOOK && (
                            <ChannelsSearch
                                searchQuery={searchQuery}
                                onSearchQuery={handleSearchQuery}
                                setCurrentCategory={setCurrentCategory}
                                currentCategory={currentCategory}
                            />
                        )}
                    </div>
                    <div className="channels-bottom">
                        <ChannelsList
                            filteredChannels={filteredChannels}
                            onEditChannel={() => setOpenEditModal(true)}
                            onDeleteChannel={() => setOpenDeleteModal(true)}
                            onOpenChannel={handleOpenChannel}
                            onTargetChannelId={onTargetChannelId}
                            paginatedChannels={paginatedChannels}
                            hasEditPermission={hasEditPermission}
                        />
                    </div>
                </div>
            </div>

            {/** Create channel modal */}
            {openCreateModal && (
                <CreateChannelModal
                    mode="new"
                    openModal={openCreateModal}
                    closeModal={() => setOpenCreateModal(false)}
                    setOnTargetChannelId={setOnTargetChannelId}
                    setCurrentChannel={setCurrentChannel}
                    setOnCreateChannel={setOnCreateChannel}
                />
            )}

            {/** Edit channel modal */}
            {openEditModal && (
                <CreateChannelModal
                    mode="edit"
                    channel={currentChannel}
                    setCurrentChannel={setCurrentChannel}
                    openModal={openEditModal}
                    closeModal={() => setOpenEditModal(false)}
                />
            )}

            {/** Delete channel modal */}
            {openDeleteModal && (
                <DeleteModal
                    open={openDeleteModal}
                    onClose={() => setOpenDeleteModal(false)}
                    elementTitle={t("DeleteModal.TheChannel", { channelName: currentChannel?.name ?? "" })}
                    onDelete={handleChannelDelete}
                    needsConfirmation={!!currentChannel?.hasTerm || !!currentChannel?.hasTermTemplate}
                    confirmationText={t("DeleteModal.TypeDelete")}
                    description={<DeleteChannelDescription channel={currentChannel} />}
                />
            )}
        </>
    );
};

export default Channels;
